import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../config/axiosConfig';

const fmtNumber = (value, decimals = 0) =>
    Number(value || 0).toLocaleString('es-CL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

const fmtDate = (value) => {
    if (!value) return '-';
    const [date] = String(value).split(' ');
    const [y, m, d] = date.split('-');
    return y && m && d ? `${d}-${m}-${y}` : value;
};

const fmtTime = (value) => {
    if (!value) return '-';
    const parts = String(value).split(' ');
    const time = parts.length > 1 ? parts[1] : parts[0];
    return time.substring(0, 5); // HH:mm
};

const fmtDayDate = (value) => {
    if (!value) return '-';
    const [date] = String(value).split(' ');
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return value;

    const dayName = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'][
        new Date(y, m - 1, d).getDay()
    ];

    return `${dayName} ${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}`;
};

function UsoCalendario2() {
    const [desde, setDesde] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [hasta, setHasta] = useState(new Date().toISOString().split('T')[0]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const cargar = async () => {
        setLoading(true);
        setError('');

        try {
            const resp = await axiosInstance.get('/auditoria-turnos/uso-calendario-detalle', {
                params: { desde, hasta },
            });

            if (resp.data?.error) {
                setError(resp.data.message || 'No se pudo cargar el reporte.');
                setData([]);
                return;
            }

            setData(resp.data?.data || []);
        } catch (err) {
            console.error(err);
            setError('Error al conectar con el servidor.');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDuration = (minutes) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h ${m}m`;
    };

    return (
        <div className="container-fluid py-4 px-4">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history fs-3 text-primary" />
                    <div>
                        <h4 className="mb-0 fw-bold">Uso Calendario 2 (Detalle por Jornada)</h4>
                        <small className="text-muted">
                            Análisis de actividad desde el primer evento hasta el último del día.
                        </small>
                    </div>
                </div>

                <div className="d-flex align-items-end gap-2">
                    <div style={{ width: 160 }}>
                        <label className="form-label small fw-semibold mb-1">Desde</label>
                        <input
                            type="date"
                            className="form-control"
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                        />
                    </div>
                    <div style={{ width: 160 }}>
                        <label className="form-label small fw-semibold mb-1">Hasta</label>
                        <input
                            type="date"
                            className="form-control"
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                        />
                    </div>
                    <button className="btn btn-primary" onClick={cargar} disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-search me-2" />}
                        Consultar
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger shadow-sm border-0">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {error}
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex align-items-center justify-content-between py-3">
                    <span className="fw-bold text-dark">Registros de Uso Diario</span>
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill">
                        {data.length} jornadas encontradas
                    </span>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light text-muted small text-uppercase fw-bold">
                                <tr>
                                    <th className="ps-4">Usuario</th>
                                    <th>Fecha</th>
                                    <th>Inicio</th>
                                    <th>Fin</th>
                                    <th>Duración Rango</th>
                                    <th>Interacciones</th>
                                    <th>Inactividad (Gaps)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted py-5">
                                            <i className="bi bi-calendar-x d-block fs-2 mb-2" />
                                            No se encontraron registros de auditoría en el periodo seleccionado.
                                        </td>
                                    </tr>
                                )}
                                {data.map((r, idx) => {
                                    const gaps30 = (r.gaps || []).filter(g => g.minutos >= 30 && g.minutos < 60).length;
                                    const gaps60 = (r.gaps || []).filter(g => g.minutos >= 60).length;

                                    return (
                                        <tr key={`${r.usuario_id}_${r.dia}_${idx}`}>
                                            <td className="ps-4">
                                                <div className="fw-bold text-dark">{r.usuario_nombre}</div>
                                                <small className="text-muted">ID: {r.usuario_id}</small>
                                            </td>
                                            <td>{fmtDayDate(r.dia)}</td>
                                            <td>
                                                <span className="badge bg-light text-dark border fw-normal">
                                                    <i className="bi bi-play-fill text-success me-1" />
                                                    {fmtTime(r.primera_accion)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark border fw-normal">
                                                    <i className="bi bi-stop-fill text-danger me-1" />
                                                    {fmtTime(r.ultima_accion)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="fw-semibold">{formatDuration(r.minutos_totales)}</div>
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.minutos_totales} minutos</div>
                                            </td>
                                            <td>
                                                <span className="badge bg-info-subtle text-info border border-info-subtle px-2">
                                                    {r.interacciones}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {gaps30 > 0 && (
                                                        <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle d-flex align-items-center" title="Espacios de 30 a 59 min sin acciones">
                                                            <i className="bi bi-exclamation-circle me-1" />
                                                            {gaps30} gap{gaps30 > 1 ? 's' : ''} &ge; 30m
                                                        </span>
                                                    )}
                                                    {gaps60 > 0 && (
                                                        <span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle d-flex align-items-center" title="Espacios de 1 hora o más sin acciones">
                                                            <i className="bi bi-exclamation-triangle-fill me-1" />
                                                            {gaps60} gap{gaps60 > 1 ? 's' : ''} &ge; 1h
                                                        </span>
                                                    )}
                                                    {gaps30 === 0 && gaps60 === 0 && (
                                                        <span className="text-muted small">Sin cortes significativos</span>
                                                    )}
                                                </div>
                                                {r.gaps && r.gaps.length > 0 && (
                                                    <div className="mt-1 small text-muted" style={{ fontSize: '0.7rem' }}>
                                                        {r.gaps.map((g, i) => (
                                                            <span key={i} className="me-2" title={`${fmtTime(g.inicio)} - ${fmtTime(g.fin)}`}>
                                                                [{fmtTime(g.inicio)}-{fmtTime(g.fin)}: {g.minutos}m]
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UsoCalendario2;
